import {BaseCommand} from './base.command';
import {ElementSnapshot, GraphNodeSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom} from 'rxjs';

/**
 * CreateNodeCommand handles node creation.
 * The command is created AFTER the node is created, capturing its data.
 * Undo deletes the created node, redo restores it.
 */
export class CreateNodeCommand extends BaseCommand<ElementSnapshot> {
  /** Id of the node this command created — used to fold immediate follow-up edits. */
  public readonly createdEntityId: string;

  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    createdNode: GraphNodeSnapshot
  ) {
    super(
      'create-node',
      'Node Creation', // fallback description
      {
        before: {nodes: [], links: []},
        after: {nodes: [createdNode], links: []}
      },
      {object: 'Node', verb: 'Creation'}
    );
    this.createdEntityId = createdNode.id;
  }

  async execute(): Promise<void> {
    // Called on redo - restore the node
    const cy = this.cyService.cy;
    if (!cy) return;

    const node = this.snapshot.after.nodes[0];
    if (!node) return;

    // Restore via backend
    await firstValueFrom(this.nodeService.restoreWithChildren([node.id]));

    // Add to Cytoscape
    if (cy.$(`#${node.id}`).length === 0) {
      cy.add({
        group: 'nodes',
        data: {...node.data} as any,
        position: {...node.position},
        classes: node.classes.join(' ')
      });

      if (node.parentId) {
        cy.$(`#${node.id}`).move({parent: node.parentId});
      }
    }

    cy.style().update();
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const node = this.snapshot.after.nodes[0];
    if (!node) return;

    // Remove from Cytoscape
    cy.$(`#${node.id}`).remove();

    // Soft-delete on backend
    await firstValueFrom(this.nodeService.deleteNode(node.id));
  }
}
